import { Button } from '@kerfjs/ui/button';
import { EmptyState } from '@kerfjs/ui/empty-state';
import { List } from '@kerfjs/ui/list';
import { Text } from '@kerfjs/ui/text';
import type { SafeHtml } from 'kerfjs';

import { CurrencyAmount } from '../../../components/currency-amount.js';
import { EdgeToEdgeTable } from '../../../components/edge-to-edge-table.js';
import type { OrgSavingsT } from '../../../data/api.js';
import { matchesSettingsSearch } from '../../../lib/settings-search.js';

export function SavingsByDemand({ savings: s, query = '' }: { savings: OrgSavingsT; query?: string }): SafeHtml {
  const demands = s.byDemand.filter((d) => matchesSettingsSearch([d.demandTitle], query));
  return (
    <section aria-label="Savings by Demand">
      <List gap="xs">
        {demands.length === 0 ? (
          <EmptyState title={query.trim() === '' ? 'No recorded outcomes yet' : 'No matching demands'} />
        ) : (
          <EdgeToEdgeTable label="Savings by Demand Table">
            <table class="grid savings-by-demand">
              <thead>
                <tr>
                  <th>Demand</th>
                  <th>Closed</th>
                  <th class="num">Best quotes</th>
                  <th class="num">Paid</th>
                  <th class="num">Saved</th>
                  <th class="num">vs benchmark</th>
                </tr>
              </thead>
              <tbody>
                {demands.map((d) => (
                  <tr data-key={d.demandId}>
                    <td>
                      <Button
                        emphasis="plain"
                        tone="brand"
                        size="compact"
                        rootAttributes={{ 'data-demand-id': d.demandId }}
                      >
                        {d.demandTitle}
                      </Button>
                    </td>
                    <td>
                      <Text variant="span" tone="quiet">
                        {d.closedAt !== null ? new Date(d.closedAt).toLocaleDateString() : '—'}
                      </Text>
                    </td>
                    <td class="num">
                      {d.bestQuotes > 0 ? <CurrencyAmount value={d.bestQuotes} currency={s.base} /> : '—'}
                    </td>
                    <td class="num">
                      <CurrencyAmount value={d.paid} currency={s.base} />
                    </td>
                    <td class="num">
                      <CurrencyAmount value={d.savings} currency={s.base} />
                    </td>
                    <td class="num">
                      <List hAlign="right">
                        <CurrencyAmount value={d.savingsVsBenchmark} currency={s.base} />
                        {d.benchmarkVersions.length > 0 ? (
                          <Text variant="span" tone="quiet" size="compact">
                            Pricing check {d.benchmarkVersions.map((version) => `v${String(version)}`).join(', ')}
                          </Text>
                        ) : null}
                      </List>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </EdgeToEdgeTable>
        )}
      </List>
    </section>
  );
}
